import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import { DragEvent } from 'react';
import { useParams } from 'react-router';
import {
  Search,
  X,
  Loader2,
  Maximize2,
  GripVerticalIcon,
  ChevronDown,
  Lock,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useNodeStore, selectSelectedNodeIds } from '@/stores/node-store';
import { useShallow } from 'zustand/react/shallow';
import { useDnD } from '@/context/dnd-context';
import { NodeStruct, NodeCategory } from '@/types/node-types';
import { NodeList } from './node-list';
import { NoResultsMessage } from './no-results-message';
import {
  getIconComponent,
  hasIcon,
} from '@/pages/workflow/helper/icon-mapping';
import { ClarificationSettingsButton } from './clarification-settings-button';
import { useClarificationPauseDetection } from '@/hooks/use-clarification-pause-detection';
import { useDebounce } from '@/hooks/use-debounce';
import { useNodeSelection } from '@/hooks/use-node-selection';
import { useReactFlow } from '@xyflow/react';
import { useViewport } from '@xyflow/react';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@/components/ui/tooltip';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Chat,
  ChatMessageData,
  type WorkflowMutationEntry,
} from '@/components/chat';
import { ChatPromptQueueStrip } from '@/components/chat/chat-prompt-queue-strip';
import { useChatPromptQueue } from '@/components/chat/use-chat-prompt-queue';
import {
  promptQueueKey,
  useChatPromptQueueStore,
} from '@/stores/chat-prompt-queue-store';
import { useOrgStore } from '@/stores/org-store';
import { v4 as uuidv4 } from 'uuid';
import {
  useChatUIStore,
  GENERAL_CHAT_MODELS,
  type GeneralChatModelId,
} from '@/stores/chat-ui-store';
import { useNodeExplorerStore } from '@/stores/node-explorer-store';
import { useWorkflowPanelStore } from '@/stores/workflow-panel-store';
import { useWorkflowStore } from '@/stores/workflow-store';
import { cn } from '@/lib/utils';
import { useNotification } from '@/components/notifications';
import { tabTriggerClasses } from './console-sidebar';
import { useResizablePanel } from '@/hooks/use-resizable-panel';
import {
  useGeneralChat,
  sanitizeStalePendingClarifications,
} from '@/hooks/use-general-chat';
import { isApplyChangesIntent } from '@/components/chat/helpers/apply-changes-intent';
import { useLaunchDarkly } from '@/providers/launchdarkly-provider';
import { CanvasAgentChat } from './canvas-agent-chat';
import { useNodeChatMode } from '@/hooks/use-node-chat-mode';
import { SelectionFooter } from './selection-footer';
import {
  VERTICAL_STACK_GAP,
  setMultiNodeDragImage,
} from '@/stores/helpers/node-drop-helpers';
import {
  DEFAULT_EXPANDED_MCP_SERVERS,
  isPickerHiddenNode,
} from '@/constants/node-catalog';
import {
  NODE_EXPLORER_PANEL_MIN_WIDTH,
  NODE_EXPLORER_PANEL_MAX_WIDTH,
} from '@/constants/workflow-editor';
import { WorkflowLockBanner } from './workflow-lock-banner';

// Shared with the editor page so producer and consumer can't drift. Several
// coexist when parallel LLM nodes ask at the same DAG level.
export interface PendingClarification {
  nodeId: string;
  displayName: string;
  question: string;
  context: string | null;
  /** Configured timeout in seconds, or ``null`` for "ask and wait"
   *  (indefinite — no countdown, the card shows "Waiting for your
   *  answer"). */
  timeoutSecs: number | null;
  /** Epoch-ms the FE received the event (or recovery time). Countdown
   *  fallback when ``deadlineEpochMs`` is missing (older BE). */
  requestedAt: number;
  /** BE-emitted absolute deadline (epoch-ms). The countdown anchors to
   *  this so WS lag / tab-throttling don't desync the visual from the
   *  BE's actual wait. ``null`` only for BE builds predating the field. */
  deadlineEpochMs: number | null;
  // Visual only; the BE deadline is what actually gates the workflow.
  isPaused: boolean;
  pausedAtEpochMs: number | null;
}

interface NodeExplorerSidebarProps {
  headerComponent?: React.ReactNode;
  panelGroupWidth?: number;
  isLocked?: boolean;
  allowedSubTypes?: Set<string>;
  isWorkflowLocked?: boolean;
  isWorkflowAdmin?: boolean;
  onUnlockWorkflow?: () => void;
  pendingClarifications?: PendingClarification[];
  onDeliverClarification?: (
    nodeId: string,
    answer: string,
    declined?: boolean,
  ) => Promise<boolean>;
  // Void promise so the typing-detection hook can swallow transport errors
  // without disturbing the typing UX.
  onPauseClarificationCountdown?: () => Promise<void>;
  /** Companion to onPauseClarificationCountdown. */
  onResumeClarificationCountdown?: () => Promise<void>;
  // A queue, not a single slot: two resolutions in one React tick used to
  // collapse into "last wins".
  resolvedClarifications?: Array<{
    requestedAt: number;
    nodeId: string;
    timedOut: boolean;
    skipped: boolean;
    abandoned: boolean;
    resolvedAt: number;
  }>;
  onConsumeResolvedClarification?: (key: {
    nodeId: string;
    requestedAt: number;
  }) => void;
  // On change, prior-session cards are force-resolved: their requestedAt can
  // never match an event scoped to the new session, so they'd hang forever.
  runSessionId?: string | null;
  // A global pause suspends the clarification timeout backend-side, so this
  // is OR-ed into each card's isPaused to freeze the countdown to match.
  isWorkflowPaused?: boolean;
  /**
   * Dispatch a run of the current workflow requested via chat. Same
   * path as the Play button; resolves to the run's id when the run
   * actually dispatched. See ``useGeneralChat`` options.
   */
  onRunWorkflow?: () => Promise<string | null>;
  /**
   * Set by the editor when a chat-initiated run ends.
   * Passed through to ``useGeneralChat`` for artifact polling.
   */
  chatRunEndedSignal?:
    | import('@/hooks/use-general-chat').ChatRunEndedSignal
    | null;
  /**
   * Open an artifact in the embedded right-side viewer.
   * Forwarded to the Chat component's ``onOpenArtifact``.
   */
  onOpenArtifact?: (
    artifact: import('@/types/artifacts').ArtifactListItem,
  ) => void;
  /**
   * Called by ``useGeneralChat`` once the artifact run signal for the given
   * ``seq`` has been fully consumed. The parent (workflow editor) should
   * clear ``chatRunEndedSignal`` only if it still holds that ``seq`` — a
   * newer signal may have already replaced it — so a re-mounted hook
   * instance doesn't reprocess an already-handled signal without dropping
   * one that wasn't.
   */
  onChatRunSignalConsumed?: (seq: number) => void;
}

type SidebarTab = 'nodes' | 'chat';

const ENABLE_CHAT = true;

const NodeExplorerSidebarContent: React.FC<{
  headerComponent?: React.ReactNode;
  isLocked?: boolean;
  allowedSubTypes?: Set<string>;
  isWorkflowLocked?: boolean;
  isWorkflowAdmin?: boolean;
  onUnlockWorkflow?: () => void;
  pendingClarifications?: PendingClarification[];
  onDeliverClarification?: (
    nodeId: string,
    answer: string,
    declined?: boolean,
  ) => Promise<boolean>;
  onPauseClarificationCountdown?: () => Promise<void>;
  onResumeClarificationCountdown?: () => Promise<void>;
  /** See ``NodeExplorerSidebarProps.resolvedClarifications``. */
  resolvedClarifications?: Array<{
    requestedAt: number;
    nodeId: string;
    timedOut: boolean;
    skipped: boolean;
    abandoned: boolean;
    resolvedAt: number;
  }>;
  onConsumeResolvedClarification?: (key: {
    nodeId: string;
    requestedAt: number;
  }) => void;
  /** See ``NodeExplorerSidebarProps.runSessionId`` for rationale. */
  runSessionId?: string | null;
  /** See ``NodeExplorerSidebarProps.isWorkflowPaused`` for rationale. */
  isWorkflowPaused?: boolean;
  /** See ``NodeExplorerSidebarProps.onRunWorkflow`` for rationale. */
  onRunWorkflow?: () => Promise<string | null>;
  /** See ``NodeExplorerSidebarProps.chatRunEndedSignal``. */
  chatRunEndedSignal?:
    | import('@/hooks/use-general-chat').ChatRunEndedSignal
    | null;
  /** See ``NodeExplorerSidebarProps.onOpenArtifact``. */
  onOpenArtifact?: (
    artifact: import('@/types/artifacts').ArtifactListItem,
  ) => void;
  /** See ``NodeExplorerSidebarProps.onChatRunSignalConsumed``. */
  onChatRunSignalConsumed?: (seq: number) => void;
}> = ({
  headerComponent,
  isLocked = false,
  allowedSubTypes,
  isWorkflowLocked,
  isWorkflowAdmin,
  onUnlockWorkflow,
  pendingClarifications,
  onDeliverClarification,
  onPauseClarificationCountdown,
  onResumeClarificationCountdown,
  resolvedClarifications,
  onConsumeResolvedClarification,
  runSessionId,
  isWorkflowPaused = false,
  onRunWorkflow,
  chatRunEndedSignal,
  onOpenArtifact,
  onChatRunSignalConsumed,
}) => {
  // FIFO-oldest unresolved entry: the target for chat-input dispatch +
  // placeholder. ``undefined`` when the queue is empty (chat input
  // falls back to general/node mode).
  const activeClarification = pendingClarifications?.[0];

  // Wire typing/hover detection to the BE pause/resume signals. No-op when
  // the callbacks are missing (injected only on the run path) or the
  // clarification is unbounded: ask_and_wait (timeoutSecs === null) has no
  // countdown, so pause/resume would be no-ops. Mode is per-run, so gating
  // on the FIFO-oldest entry is representative.
  const noopAsync = useCallback(async () => undefined, []);
  const { onInputChange, setCardHoverState, reset, notifyExternalResume } =
    useClarificationPauseDetection({
      isClarificationActive:
        !!activeClarification &&
        activeClarification.timeoutSecs !== null &&
        !!onPauseClarificationCountdown &&
        !!onResumeClarificationCountdown,
      onPauseSignal: onPauseClarificationCountdown ?? noopAsync,
      onResumeSignal: onResumeClarificationCountdown ?? noopAsync,
    });

  // Detect any BE resume edge (safety-cap force-resume or FE-driven) by
  // watching the queue's collective ``isPaused`` flag go true → false
  // (pause is workflow-global, so ``some(isPaused)`` is a safe proxy).
  // Without this, the hook's ``fePauseFiredRef`` dedup stays true after
  // a 60s safety-cap auto-resume and a later keystroke wouldn't re-send
  // pause, leaving the countdown ticking while the user composes.
  // ``notifyExternalResume`` is idempotent, so FE-driven resumes that
  // also hit this branch are harmless.
  const anyCardPaused = (pendingClarifications ?? []).some((p) => p.isPaused);
  const prevAnyCardPausedRef = useRef(anyCardPaused);
  useEffect(() => {
    if (prevAnyCardPausedRef.current && !anyCardPaused) {
      notifyExternalResume();
    }
    prevAnyCardPausedRef.current = anyCardPaused;
  }, [anyCardPaused, notifyExternalResume]);

  // Resets only when the queue fully drains: the user may be typing in card B
  // while card A resolves, and pause is workflow-global anyway.
  const hasAnyPending = (pendingClarifications?.length ?? 0) > 0;
  useEffect(() => {
    if (!hasAnyPending) reset();
  }, [hasAnyPending, reset]);

  // Pause-on-typing flows from the bottom chat input, which doubles as the
  // answer field. The hook early-returns when no question is active, so
  // general/node chat typing can't spuriously pause.
  const { groupedNodes, isLoadingNodeList, addNode, addNodes } = useNodeStore();
  const [, setDndPayload] = useDnD();
  const {
    isSelected: isSidebarNodeSelected,
    getCount: getSidebarSelectionCount,
    selectSingle: sidebarSelectSingle,
    selectMultiple: sidebarSelectMultiple,
    toggleSelect: toggleSidebarSelect,
    selectRange: sidebarSelectRange,
    addToSelection: sidebarAddToSelection,
    clearSelection: clearSidebarSelection,
    selectedNodeIds: sidebarSelectedNodeIds,
    selectionCounts: sidebarSelectionCounts,
    totalSelectedCount: sidebarTotalSelectedCount,
  } = useNodeSelection();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [openCategories, setOpenCategories] = useState<string[]>([
    NodeCategory.AGENT,
  ]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const listContainerRef = useRef<HTMLDivElement>(null);
  const [flatDisplayOrderFromList, setFlatDisplayOrderFromList] = useState<
    NodeStruct[] | null
  >(null);
  const { setCenter, screenToFlowPosition } = useReactFlow();

  // Mirrors handleViewNode in errors-tab-content.tsx but skips
  // openWorkflowPanel: the user asked to focus the node, not configure it.
  const handleFocusClarificationNode = useCallback(
    (nodeId: string) => {
      const { nodes, selectNode, deselectAllNodes } = useNodeStore.getState();
      const node = nodes.find((n) => n.id === nodeId);
      if (node) {
        setCenter(node.position.x + 100, node.position.y + 50, {
          zoom: 1,
          duration: 500,
        });
      }
      deselectAllNodes();
      selectNode(nodeId);
    },
    [setCenter],
  );

  // Live name resolver for clarification cards: reads the current
  // ``useNodeStore`` (same ``user_config_name || display_name``
  // precedence as the canvas) so a mid-run rename isn't shown stale.
  // ``undefined`` when the node is gone, so the card falls back to the
  // message's ``nodeDisplayName``.
  const resolveClarificationNodeName = useCallback(
    (nodeId: string): string | undefined => {
      const { nodes } = useNodeStore.getState();
      const node = nodes.find((n) => n.id === nodeId);
      const struct = node?.data?.struct;
      return struct?.user_config_name || struct?.display_name || undefined;
    },
    [],
  );

  // Returns undefined when the node is gone or its icon is unknown, so the
  // card falls back to the question-mark.
  const resolveClarificationNodeIcon = useCallback(
    (nodeId: string): React.ReactNode | undefined => {
      const { nodes } = useNodeStore.getState();
      const node = nodes.find((n) => n.id === nodeId);
      const struct = node?.data?.struct;
      if (!struct?.icon || !hasIcon(struct.icon)) return undefined;
      return getIconComponent(struct.icon, struct.icon_color, 'w-3.5 h-3.5');
    },
    [],
  );
  const { activeTab, setActiveTab } = useNodeExplorerStore();
  const { setActiveTab: setConsoleSidebarActiveTab, setConsolePanelOpen } =
    useWorkflowPanelStore();
  const { selectedModel, setSelectedModel, isGeneralChatActive } =
    useChatUIStore();
  const notification = useNotification();
  const [showModelSelector, setShowModelSelector] = useState(false);
  const modelSelectorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showModelSelector) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        modelSelectorRef.current &&
        !modelSelectorRef.current.contains(e.target as Node)
      ) {
        setShowModelSelector(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showModelSelector]);

  const { workflowId } = useParams<{ workflowId: string }>();
  const { isCanvasA2aChatEnabled } = useLaunchDarkly();
  const selectedNodeIds = useNodeStore(useShallow(selectSelectedNodeIds));
  const selectedNodeId = selectedNodeIds[0] ?? null;

  // --- Unified chat hook (single WS, adaptive context, all tools) ---
  const {
    chatMessages,
    applyMessage,
    isGeneralChatConnecting,
    isGeneralChatInputBusy,
    isGeneralChatReady,
    selectedNodeDisplayName,
    sendGeneralChatMessage,
    handleApplyUpdates,
    handleApplyWorkflowMutations,
    applyBufferedMutations,
    applyPendingChatProposals,
    handleConfirmRemoveNode,
    insertDivider,
    ensureConnection,
    isEditingLocked: isChatEditingLocked,
  } = useGeneralChat(selectedNodeId, workflowId, runSessionId, {
    onRunWorkflow,
    chatRunEndedSignal,
    onChatRunSignalConsumed,
    isCanvasLocked: isLocked,
  });

  const openWorkflowKey = useWorkflowStore((s) =>
    s.currentWorkflow?.id && s.currentWorkflow.id !== 'new'
      ? s.currentWorkflow.id
      : `new:${s.currentWorkflow?.createdAt ?? ''}`,
  );
  // Same guard as dispatchCanvasApplyMutations: a proposal frozen against
  // one workflow must not land on another the canvas has since switched to.
  const applyCanvasChatMutations = useCallback(
    (
      mutations: WorkflowMutationEntry[],
      targetWorkflowId?: string | null,
      applyKey?: string,
    ) => {
      if (targetWorkflowId != null && targetWorkflowId !== openWorkflowKey) {
        return false;
      }
      return applyBufferedMutations(mutations, applyKey);
    },
    [applyBufferedMutations, openWorkflowKey],
  );

  // --- Node chat mode hook (live node chat WS, mode transitions, dividers) ---
  const {
    effectiveChatMode,
    isChatConnecting,
    isChatConnected,
    isChatDenied,
    isChatAwaitingInput,
    isChatLoading,
    handleContinue,
    sendNodeChatMessage,
  } = useNodeChatMode({
    selectedNodeId,
    applyMessage,
    insertDivider,
    ensureConnection,
  });

  const { zoom } = useViewport();

  // Dedupe key is nodeId:requestedAt -- a node can ask twice in one run. Keys
  // persist after an entry leaves the queue so a rerun doesn't re-announce.
  const announcedKeysRef = useRef<Map<string, string>>(new Map());
  useEffect(() => {
    if (!pendingClarifications || pendingClarifications.length === 0) return;

    // Perf short-circuit: this effect depends on ``chatMessages`` (for
    // the hydration scan) so it re-runs on every chat update. Each key
    // only needs one announce/reuse pass, so bail cheaply BEFORE the
    // O(chatMessages) scan when nothing is unannounced — keeping the
    // steady-state cost O(pendingClarifications), not O(chatMessages)
    // per streamed token.
    let hasUnannounced = false;
    for (const p of pendingClarifications) {
      if (!announcedKeysRef.current.has(`${p.nodeId}:${p.requestedAt}`)) {
        hasUnannounced = true;
        break;
      }
    }
    if (!hasUnannounced) return;

    // After a reload the restored card keeps its original requestedAt while the
    // recovery fetch stamps a fresh one, so announcing naively would mint a
    // second card that no resolution could ever match. Reuse the hydrated card
    // instead, keeping its id stable so the textarea doesn't remount.
    const hydratedByNodeId = new Map<string, ChatMessageData>();
    for (const m of chatMessages) {
      if (
        m.variant !== 'clarification_request' ||
        !m.clarificationRequest ||
        m.clarificationResolution !== undefined
      ) {
        continue;
      }
      if (runSessionId && m.clarificationRequest.sessionId !== runSessionId) {
        continue;
      }
      hydratedByNodeId.set(m.clarificationRequest.nodeId, m);
    }

    const reuseUpdates = new Map<
      string,
      {
        requestedAt: number;
        deadlineEpochMs?: number | null;
        timeoutSecs: number | null;
      }
    >();
    const newCards: ChatMessageData[] = [];
    for (const p of pendingClarifications) {
      const key = `${p.nodeId}:${p.requestedAt}`;
      if (announcedKeysRef.current.has(key)) continue;
      const hydrated = hydratedByNodeId.get(p.nodeId);
      if (hydrated) {
        announcedKeysRef.current.set(key, hydrated.id);
        reuseUpdates.set(hydrated.id, {
          requestedAt: p.requestedAt,
          deadlineEpochMs: p.deadlineEpochMs,
          // Sync ``timeoutSecs`` too so an ``ask_and_wait`` recovery
          // (``null``) flips a stale numeric card to "Waiting for your
          // answer" rather than leaving a phantom countdown.
          timeoutSecs: p.timeoutSecs,
        });
        // One hydrated card per nodeId; "same node asks twice"
        // surfaces as two distinct entries and the second one
        // should mint a fresh card.
        hydratedByNodeId.delete(p.nodeId);
        continue;
      }
      const messageId = uuidv4();
      announcedKeysRef.current.set(key, messageId);
      newCards.push({
        id: messageId,
        role: 'assistant',
        content: '',
        timestamp: new Date(),
        variant: 'clarification_request',
        clarificationRequest: {
          nodeId: p.nodeId,
          nodeDisplayName: p.displayName,
          question: p.question,
          context: p.context,
          requestedAt: p.requestedAt,
          timeoutSecs: p.timeoutSecs,
          deadlineEpochMs: p.deadlineEpochMs,
          // Lets a future hydrate tell this card from cross-session leftovers.
          sessionId: runSessionId ?? undefined,
        },
      });
    }
    if (newCards.length > 0 || reuseUpdates.size > 0) {
      applyMessage((prev) => {
        const refreshed =
          reuseUpdates.size > 0
            ? prev.map((m) => {
                const update = reuseUpdates.get(m.id);
                if (!update || !m.clarificationRequest) return m;
                return {
                  ...m,
                  clarificationRequest: {
                    ...m.clarificationRequest,
                    requestedAt: update.requestedAt,
                    deadlineEpochMs: update.deadlineEpochMs,
                    timeoutSecs: update.timeoutSecs,
                  },
                };
              })
            : prev;
        return newCards.length > 0 ? [...refreshed, ...newCards] : refreshed;
      });
    }
  }, [pendingClarifications, chatMessages, applyMessage, runSessionId]);

  // Kept separate from the announce effect: a pure synchroniser that runs on
  // every pending-list change and no-ops when nothing moved.
  useEffect(() => {
    if (!pendingClarifications || pendingClarifications.length === 0) return;
    applyMessage((prev) => {
      let mutated = false;
      const next = prev.map((m) => {
        if (m.variant !== 'clarification_request' || !m.clarificationRequest) {
          return m;
        }
        const matchingPending = pendingClarifications.find(
          (p) =>
            p.nodeId === m.clarificationRequest!.nodeId &&
            p.requestedAt === m.clarificationRequest!.requestedAt,
        );
        if (!matchingPending) return m;
        const currentReq = m.clarificationRequest;
        // A global workflow pause also freezes the card countdown: the
        // backend suspends the clarification timeout while paused, so the
        // ticking number would be misleading. OR it into the per-card
        // (typing/manual) pause flag the countdown already honours.
        const effectivePaused = matchingPending.isPaused || isWorkflowPaused;
        if (
          currentReq.isPaused === effectivePaused &&
          currentReq.pausedAtEpochMs === matchingPending.pausedAtEpochMs &&
          currentReq.deadlineEpochMs === matchingPending.deadlineEpochMs
        ) {
          return m;
        }
        mutated = true;
        return {
          ...m,
          clarificationRequest: {
            ...currentReq,
            isPaused: effectivePaused,
            pausedAtEpochMs: matchingPending.pausedAtEpochMs,
            deadlineEpochMs: matchingPending.deadlineEpochMs,
          },
        };
      });
      return mutated ? next : prev;
    });
  }, [pendingClarifications, applyMessage, isWorkflowPaused]);

  // Covers in-memory run → next-run boundaries; the IDB-hydration sanitiser in
  // use-general-chat covers reloads.
  useEffect(() => {
    if (runSessionId === undefined) return;
    applyMessage((prev) =>
      sanitizeStalePendingClarifications(prev, runSessionId),
    );
  }, [runSessionId, applyMessage]);

  // Matches on nodeId AND requestedAt: recovered entries share one requestedAt,
  // so matching on that alone would flip every recovered card at once, while
  // nodeId alone can't keep "same node asks twice" independent.
  useEffect(() => {
    if (!resolvedClarifications || resolvedClarifications.length === 0) return;
    const resolutionByKey = new Map<
      string,
      { timedOut: boolean; skipped: boolean; abandoned: boolean }
    >();
    for (const r of resolvedClarifications) {
      resolutionByKey.set(`${r.nodeId}::${r.requestedAt}`, {
        timedOut: r.timedOut,
        skipped: r.skipped,
        abandoned: r.abandoned,
      });
    }
    applyMessage((prev) =>
      prev.map((m) => {
        if (m.variant !== 'clarification_request' || !m.clarificationRequest) {
          return m;
        }
        const key = `${m.clarificationRequest.nodeId}::${m.clarificationRequest.requestedAt}`;
        const resolution = resolutionByKey.get(key);
        if (!resolution) return m;
        if (m.clarificationResolution !== undefined) {
          // Already resolved locally (handleSendMessage path);
          // preserve the user's typed answer and skip overwriting.
          return m;
        }
        return {
          ...m,
          clarificationResolution: resolution.abandoned
            ? 'abandoned'
            : resolution.skipped
              ? 'skipped'
              : resolution.timedOut
                ? 'timed_out'
                : 'answered',
        };
      }),
    );
    for (const r of resolvedClarifications) {
      onConsumeResolvedClarification?.({
        nodeId: r.nodeId,
        requestedAt: r.requestedAt,
      });
    }
  }, [resolvedClarifications, applyMessage, onConsumeResolvedClarification]);

  // Returns false when nothing went out (socket gone): the prompt-queue
  // dispatch requeues the message instead of losing it.
  const handleSendMessage = useCallback(
    (message: string): boolean => {
      if (effectiveChatMode === 'general' && isApplyChangesIntent(message)) {
        if (applyPendingChatProposals() === 'none-pending') {
          notification.info('There are no pending changes to apply.');
        }
        return true;
      }

      const userMessage: ChatMessageData = {
        id: uuidv4(),
        role: 'user',
        content: message,
        timestamp: new Date(),
      };
      applyMessage((prev) => [...prev, userMessage]);

      if (effectiveChatMode === 'general') {
        const sent = sendGeneralChatMessage(message);
        if (!sent) {
          applyMessage((prev) => prev.filter((m) => m.id !== userMessage.id));
          notification.error('Chat is not connected. Please try again.');
          return false;
        }
        return true;
      }
      const sent = sendNodeChatMessage(message);
      if (!sent) {
        // Nothing went out -- drop the optimistic echo so a requeued
        // prompt doesn't leave a phantom user message behind.
        applyMessage((prev) => prev.filter((m) => m.id !== userMessage.id));
        return false;
      }
      return true;
    },
    [
      effectiveChatMode,
      applyPendingChatProposals,
      applyMessage,
      sendGeneralChatMessage,
      sendNodeChatMessage,
      notification,
    ],
  );

  // --- Prompt queue: follow-ups typed while the agent is busy ---
  // Same store and UX as agent-chat's queue (prompt-queue-strip /
  // use-prompt-queue-dispatch); the signals below are the legacy stack's
  // equivalents of isSending / write access.
  const orgId = useOrgStore((s) => s.currentOrg?.orgid);
  const isNodeMode = effectiveChatMode === 'node';
  // A turn is in flight for the current mode -- exactly what used to
  // disable the composer. Node chat: connected but not awaiting input
  // (isChatLoading alone flickers off on each streamed message). General
  // chat: busy until the awaiting_user_input frame.
  const isAgentBusy = isNodeMode
    ? isChatConnected &&
      !isChatDenied &&
      (isChatLoading || !isChatAwaitingInput)
    : isGeneralChatInputBusy;
  // The current mode's session could take a message right now; while
  // false, dispatch keeps prompts queued rather than sending into a
  // closed/denied session.
  const canSendNow = isNodeMode
    ? isChatConnected && isChatAwaitingInput && !isChatDenied
    : isGeneralChatActive && isGeneralChatReady;
  const hasPendingClarificationCard = (pendingClarifications?.length ?? 0) > 0;
  // Keyed per workflow AND per mode: a mode flip (node session ending ->
  // general) must never dispatch one mode's prompts into the other's
  // session. Node-chat prompts are per-workflow, not per-node -- the live
  // session follows whichever node paused, not the canvas selection.
  const chatQueueKey = promptQueueKey(
    orgId,
    `canvas:${workflowId ?? ''}`,
    effectiveChatMode,
  );
  // Node follow-ups are addressed to one paused node's conversation. Once
  // that session ends (Resume, disconnect, or a reload) nothing can take
  // them, and keeping them would fire them into the NEXT paused node.
  const nodeQueueKey = promptQueueKey(
    orgId,
    `canvas:${workflowId ?? ''}`,
    'node',
  );
  const clearPromptQueue = useChatPromptQueueStore((s) => s.clear);
  useEffect(() => {
    if (!isNodeMode) clearPromptQueue(nodeQueueKey);
  }, [isNodeMode, nodeQueueKey, clearPromptQueue]);

  const promptQueue = useChatPromptQueue({
    queueKey: chatQueueKey,
    isBusy: isAgentBusy,
    canSend: canSendNow,
    hasPendingClarification: hasPendingClarificationCard,
    sendMessage: handleSendMessage,
  });

  // Click-to-edit from the queue strip pops a prompt back into the
  // composer; ChatInput registers the setter on mount.
  const composerDraftSetterRef = useRef<((text: string) => void) | null>(null);
  const registerComposerDraftSetter = useCallback(
    (setter: (text: string) => void) => {
      composerDraftSetterRef.current = setter;
      return () => {
        if (composerDraftSetterRef.current === setter) {
          composerDraftSetterRef.current = null;
        }
      };
    },
    [],
  );
  // Reports false when no composer is mounted, so the strip keeps the
  // prompt queued instead of dropping it.
  const setComposerDraft = useCallback((text: string): boolean => {
    const setter = composerDraftSetterRef.current;
    if (!setter) return false;
    setter(text);
    return true;
  }, []);

  // The BE parks until every question in a batch is answered and overwrites a
  // slot on re-delivery, so an answer with siblings outstanding is stashed as a
  // revisable draft; answering the last one flips the whole batch at once.
  // A failed POST rolls the card (and any optimistically flipped siblings) back.
  const handleDeliverAnswer = useCallback(
    async (
      nodeId: string,
      answer: string,
      declined = false,
    ): Promise<boolean> => {
      if (!onDeliverClarification) return false;
      // Does answering this node complete the batch?  True when no
      // OTHER pending clarification still lacks a draft.
      const completesBatch = !chatMessages.some(
        (m) =>
          m.variant === 'clarification_request' &&
          !!m.clarificationRequest &&
          m.clarificationResolution === undefined &&
          m.clarificationRequest.nodeId !== nodeId &&
          !m.clarificationRequest.submitted,
      );
      // Match the mutated message *by id* so a failure rolls back
      // precisely. nodeId alone is unsafe: a node may have an OLDER
      // resolved card (it can ask up to 2x/run) the revert must not clobber.
      const targetMessageId = chatMessages.find(
        (m) =>
          m.variant === 'clarification_request' &&
          !!m.clarificationRequest &&
          m.clarificationResolution === undefined &&
          m.clarificationRequest.nodeId === nodeId,
      )?.id;
      // Cards optimistically resolved this call (batch-completing path
      // only), captured by id so a failure reverts siblings to drafts.
      const resolvedMessageIds = completesBatch
        ? chatMessages
            .filter(
              (m) =>
                m.variant === 'clarification_request' &&
                !!m.clarificationRequest &&
                m.clarificationResolution === undefined,
            )
            .map((m) => m.id)
        : [];
      applyMessage((prev) =>
        prev.map((m) => {
          if (
            m.variant !== 'clarification_request' ||
            !m.clarificationRequest ||
            m.clarificationResolution !== undefined
          ) {
            return m;
          }
          const isTarget = m.id === targetMessageId;
          if (completesBatch) {
            // Finalise the whole batch.  Each card resolves using its
            // own drafted state (the target uses this call's args).
            const wasDeclined = isTarget
              ? declined
              : !!m.clarificationRequest.submittedDeclined;
            const finalAnswer = isTarget
              ? declined
                ? undefined
                : answer
              : m.clarificationRequest.userAnswer;
            return {
              ...m,
              clarificationResolution: wasDeclined ? 'skipped' : 'answered',
              clarificationRequest: {
                ...m.clarificationRequest,
                userAnswer: finalAnswer,
              },
            };
          }
          if (isTarget) {
            // Draft only -- keep it in the sticky set for revision.
            return {
              ...m,
              clarificationRequest: {
                ...m.clarificationRequest,
                submitted: true,
                submittedDeclined: declined,
                userAnswer: declined ? undefined : answer,
              },
            };
          }
          return m;
        }),
      );
      const ok = await onDeliverClarification(nodeId, answer, declined);
      if (!ok) {
        // Roll back.  The editor-level handler already restored the BE
        // pending entry + surfaced a notification.
        applyMessage((prev) =>
          prev.map((m) => {
            if (
              m.variant !== 'clarification_request' ||
              !m.clarificationRequest
            ) {
              return m;
            }
            if (m.id === targetMessageId) {
              // This delivery failed -> fully un-submit so the question
              // returns to the active sticky card.
              return {
                ...m,
                clarificationResolution: undefined,
                clarificationRequest: {
                  ...m.clarificationRequest,
                  submitted: false,
                  submittedDeclined: false,
                  userAnswer: undefined,
                },
              };
            }
            if (
              resolvedMessageIds.includes(m.id) &&
              m.clarificationResolution !== undefined
            ) {
              // Was optimistically resolved on the batch-completing
              // path; this card's own draft is still valid on the BE,
              // so restore it to a draft rather than unanswered.
              return { ...m, clarificationResolution: undefined };
            }
            return m;
          }),
        );
      }
      return ok;
    },
    [onDeliverClarification, applyMessage, chatMessages],
  );

  const RESUME_WORKFLOW_MESSAGE = 'Resume workflow';

  const handleToggleSelect = useCallback(
    (node: NodeStruct) => {
      toggleSidebarSelect(node.node_id);
    },
    [toggleSidebarSelect],
  );

  const handleSelectSingle = useCallback(
    (node: NodeStruct) => {
      sidebarSelectSingle(node.node_id);
    },
    [sidebarSelectSingle],
  );

  const handleDragStart = useCallback(
    (event: DragEvent<HTMLDivElement>, node: NodeStruct) => {
      if (isLocked) {
        event.preventDefault();
        return;
      }
      if (allowedSubTypes && !allowedSubTypes.has(node.node_sub_type)) {
        event.preventDefault();
        return;
      }

      // If the dragged node is part of a multi-selection, drag all selected nodes
      if (
        sidebarSelectedNodeIds.has(node.node_id) &&
        sidebarSelectedNodeIds.size > 1
      ) {
        const allNodes = Object.values(groupedNodes).flat();
        const selectedIds = allNodes
          .filter((n) => sidebarSelectedNodeIds.has(n.node_id))
          .flatMap((n) => {
            const count = sidebarSelectionCounts.get(n.node_id) ?? 1;
            return Array(count).fill(n.node_id) as string[];
          });

        setDndPayload({ type: 'multiSelect', nodeIds: selectedIds });
        setMultiNodeDragImage(event, selectedIds.length);
      } else {
        const count = sidebarSelectionCounts.get(node.node_id) ?? 1;
        if (count > 1) {
          setDndPayload({
            type: 'multiSelect',
            nodeIds: Array(count).fill(node.node_id) as string[],
          });
          setMultiNodeDragImage(event, count);
        } else {
          setDndPayload({ type: 'node', nodeId: node.node_id });
        }
      }
      event.dataTransfer.effectAllowed = 'move';
    },
    [
      setDndPayload,
      isLocked,
      sidebarSelectedNodeIds,
      sidebarSelectionCounts,
      groupedNodes,
    ],
  );

  const handleDragStartCategory = useCallback(
    (
      event: DragEvent<HTMLDivElement>,
      categoryName: string,
      nodes: NodeStruct[],
    ) => {
      if (isLocked) {
        event.preventDefault();
        return;
      }

      // Check if this category's nodes overlap with the current multi-selection
      const categoryNodeIds = new Set(nodes.map((n) => n.node_id));
      const hasSelectedFromCategory = nodes.some((n) =>
        sidebarSelectedNodeIds.has(n.node_id),
      );

      if (
        hasSelectedFromCategory &&
        sidebarSelectedNodeIds.size > categoryNodeIds.size
      ) {
        // Multi-selection spans beyond this category — drag all selected nodes
        const allNodes = Object.values(groupedNodes).flat();
        const selectedIds = allNodes
          .filter((n) => sidebarSelectedNodeIds.has(n.node_id))
          .flatMap((n) => {
            const count = sidebarSelectionCounts.get(n.node_id) ?? 1;
            return Array(count).fill(n.node_id) as string[];
          });
        setDndPayload({ type: 'multiSelect', nodeIds: selectedIds });
        setMultiNodeDragImage(event, selectedIds.length);
      } else {
        // Only this category is selected — drag just its nodes
        setDndPayload({
          type: 'toolCategory',
          categoryName,
          nodeIds: nodes.map((n) => n.node_id),
        });
        setMultiNodeDragImage(event, nodes.length);
      }
      event.dataTransfer.effectAllowed = 'move';
    },
    [
      setDndPayload,
      isLocked,
      sidebarSelectedNodeIds,
      sidebarSelectionCounts,
      groupedNodes,
    ],
  );

  const handleAddCategory = useCallback(
    (_categoryName: string, nodes: NodeStruct[]) => {
      if (isLocked || nodes.length === 0) return;
      const filtered = allowedSubTypes
        ? nodes.filter((n) => allowedSubTypes.has(n.node_sub_type))
        : nodes;
      if (filtered.length === 0) return;
      const centerFlow = screenToFlowPosition({
        x: window.innerWidth / 2,
        y: window.innerHeight / 2,
      });
      addNodes(
        filtered.map((struct, index) => ({
          struct,
          position: {
            x: centerFlow.x,
            y: centerFlow.y + index * VERTICAL_STACK_GAP,
          },
        })),
      );
      setConsoleSidebarActiveTab('configure');
      setConsolePanelOpen(true);
    },
    [
      addNodes,
      screenToFlowPosition,
      setConsoleSidebarActiveTab,
      setConsolePanelOpen,
      isLocked,
    ],
  );

  const handleAddNode = useCallback(
    (node: NodeStruct) => {
      if (isLocked) return;
      if (allowedSubTypes && !allowedSubTypes.has(node.node_sub_type)) return;
      clearSidebarSelection();
      const { panelSize: rightSidebarWidthPx } =
        useWorkflowPanelStore.getState();
      addNode(node, undefined, {
        setCenter,
        zoom,
        rightSidebarWidthPx,
      });
      setConsoleSidebarActiveTab('configure');
      setConsolePanelOpen(true);
    },
    [
      addNode,
      setCenter,
      zoom,
      setConsoleSidebarActiveTab,
      setConsolePanelOpen,
      clearSidebarSelection,
      isLocked,
      allowedSubTypes,
    ],
  );

  const handleAddAllSelected = useCallback(() => {
    if (isLocked || sidebarSelectionCounts.size === 0) return;
    const allNodes = Object.values(groupedNodes).flat();
    let structs = allNodes
      .filter((n) => sidebarSelectedNodeIds.has(n.node_id))
      .flatMap((n) => {
        const count = sidebarSelectionCounts.get(n.node_id) ?? 1;
        return Array.from({ length: count }, () => n);
      });
    if (allowedSubTypes) {
      structs = structs.filter((n) => allowedSubTypes.has(n.node_sub_type));
    }
    if (structs.length === 0) return;
    const centerFlow = screenToFlowPosition({
      x: window.innerWidth / 2,
      y: window.innerHeight / 2,
    });
    addNodes(
      structs.map((struct, index) => ({
        struct,
        position: {
          x: centerFlow.x,
          y: centerFlow.y + index * VERTICAL_STACK_GAP,
        },
      })),
    );
    clearSidebarSelection();
    setConsoleSidebarActiveTab('configure');
    setConsolePanelOpen(true);
  }, [
    isLocked,
    groupedNodes,
    sidebarSelectedNodeIds,
    sidebarSelectionCounts,
    addNodes,
    screenToFlowPosition,
    clearSidebarSelection,
    setConsoleSidebarActiveTab,
    setConsolePanelOpen,
  ]);

  const handleKeyDownInput = useCallback(
    (e: React.KeyboardEvent<HTMLElement>, name: string) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setOpenCategories((prev) =>
          prev.includes(name)
            ? prev.filter((cat) => cat !== name)
            : [...prev, name],
        );
      }
    },
    [],
  );

  const filteredNodes = useMemo(() => {
    const base: Record<string, NodeStruct[]> = {};
    Object.entries(groupedNodes).forEach(([category, nodes]) => {
      const visible = nodes.filter((n) => !isPickerHiddenNode(n));
      if (visible.length > 0) base[category] = visible;
    });

    if (!debouncedSearch.trim()) return base;

    const query = debouncedSearch.toLowerCase();
    const filtered: Record<string, NodeStruct[]> = {};

    Object.entries(base).forEach(([category, nodes]) => {
      const matchingNodes = nodes.filter((node) =>
        node.display_name.toLowerCase().includes(query),
      );

      if (matchingNodes.length > 0) {
        filtered[category] = matchingNodes;
      }
    });

    return filtered;
  }, [groupedNodes, debouncedSearch]);

  const sortedCategories = useMemo(() => {
    return Object.keys(filteredNodes).sort(
      (a, b) =>
        (filteredNodes[a]?.length || 0) - (filteredNodes[b]?.length || 0),
    );
  }, [filteredNodes]);

  const computedFlatVisibleNodes = useMemo(() => {
    const nodes: NodeStruct[] = [];
    sortedCategories.forEach((category) => {
      const isExpanded =
        openCategories.includes(category) || debouncedSearch.length > 0;
      if (isExpanded) {
        const categoryNodes = filteredNodes[category] || [];
        nodes.push(...categoryNodes);
      }
    });
    return nodes;
  }, [sortedCategories, openCategories, debouncedSearch, filteredNodes]);

  const flatVisibleNodes = flatDisplayOrderFromList ?? computedFlatVisibleNodes;

  // Precedence: general-chat mode → connection/lock status; node-chat mode →
  // node state. Pending clarifications don't change it -- answers are
  // delivered inside each card.
  const chatPlaceholder = useMemo(() => {
    // Truncate long user_config_names before interpolating into the
    // placeholder.  The chat input is a textarea whose auto-resize
    // measures ``scrollHeight`` (which includes wrapped placeholder
    // content) so a 60+ char node name grows the input to 2-3 lines
    // before the user has typed anything.  28 chars fits one line
    // in the narrowest sidebar layout; longer names get an ellipsis.
    const PLACEHOLDER_NAME_LIMIT = 28;
    const truncatedName =
      selectedNodeDisplayName &&
      selectedNodeDisplayName.length > PLACEHOLDER_NAME_LIMIT
        ? `${selectedNodeDisplayName.slice(0, PLACEHOLDER_NAME_LIMIT - 1)}…`
        : selectedNodeDisplayName;
    // Busy states invite a queued follow-up: typing stays enabled and the
    // send action enqueues (see onQueueMessage on the Chat below).
    const queueHint = 'Add a follow-up — it sends when this reply finishes...';
    if (effectiveChatMode === 'node') {
      if (!isChatAwaitingInput) {
        return queueHint;
      }
      return `Chat with ${truncatedName || 'this node'}...`;
    }
    if (!selectedNodeId) {
      if (!isGeneralChatActive || isGeneralChatConnecting)
        return 'Connecting to Operator...';
      if (!isGeneralChatReady) return queueHint;
      if (isWorkflowLocked)
        return 'Workflow is locked — ask questions or debug...';
      return 'Ask about the workflow...';
    }
    if (!isGeneralChatActive || isGeneralChatConnecting) return 'Connecting...';
    if (!isGeneralChatReady) return queueHint;
    return `Ask about ${truncatedName || 'this node'}...`;
  }, [
    selectedNodeId,
    isGeneralChatActive,
    isGeneralChatConnecting,
    isGeneralChatReady,
    isWorkflowLocked,
    effectiveChatMode,
    isChatAwaitingInput,
    selectedNodeDisplayName,
  ]);

  // Workflow lock is admin-imposed (persisted) and takes precedence in the
  // message since unlocking the canvas padlock alone wouldn't resolve it.
  const applyDisabledReason = isWorkflowLocked
    ? 'Workflow is locked by an admin — ask them to unlock it to apply chat-proposed changes.'
    : 'Canvas is locked — click the canvas lock icon to unlock it and apply chat-proposed changes.';

  const handleShiftSelect = useCallback(
    (node: NodeStruct) => {
      const orderedNodeIds = flatVisibleNodes.map((n) => n.node_id);
      sidebarSelectRange(node.node_id, orderedNodeIds);
    },
    [sidebarSelectRange, flatVisibleNodes],
  );

  const handleSelectCategory = useCallback(
    (nodes: NodeStruct[]) => {
      sidebarSelectMultiple(nodes.map((n) => n.node_id));
    },
    [sidebarSelectMultiple],
  );

  const handleAddToSelection = useCallback(
    (nodes: NodeStruct[]) => {
      sidebarAddToSelection(nodes.map((n) => n.node_id));
    },
    [sidebarAddToSelection],
  );

  const handleRangeSelect = useCallback(
    (targetNodeId: string, allOrderedNodeIds: string[]) => {
      sidebarSelectRange(targetNodeId, allOrderedNodeIds);
    },
    [sidebarSelectRange],
  );

  // Clear sidebar selection when clicking outside the node list container
  useEffect(() => {
    const handleDocumentClick = (e: MouseEvent) => {
      if (
        listContainerRef.current &&
        !listContainerRef.current.contains(e.target as Node)
      ) {
        clearSidebarSelection();
      }
    };
    document.addEventListener('click', handleDocumentClick);
    return () => document.removeEventListener('click', handleDocumentClick);
  }, [clearSidebarSelection]);

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [flatVisibleNodes.length, debouncedSearch]);

  useEffect(() => {
    if (highlightedIndex >= 0 && listContainerRef.current) {
      const highlightedElement = listContainerRef.current.querySelector(
        `[data-node-index="${highlightedIndex}"]`,
      );
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex]);

  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const totalItems = flatVisibleNodes.length;
      if (totalItems === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex((prev) => (prev < totalItems - 1 ? prev + 1 : 0));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : totalItems - 1));
          break;
        case 'Enter':
          e.preventDefault();
          if (
            highlightedIndex >= 0 &&
            highlightedIndex < flatVisibleNodes.length
          ) {
            handleAddNode(flatVisibleNodes[highlightedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          setHighlightedIndex(-1);
          clearSidebarSelection();
          break;
      }
    },
    [flatVisibleNodes, highlightedIndex, handleAddNode, clearSidebarSelection],
  );

  const handleClearSearch = useCallback(() => {
    setSearch('');
  }, []);

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(event.target.value);
    },
    [],
  );

  useEffect(() => {
    if (debouncedSearch !== '') {
      const newOpenCategories = Object.keys(filteredNodes).filter(
        (cat) => Object.keys(filteredNodes[cat] || {}).length > 0,
      );
      setOpenCategories(newOpenCategories);
    } else {
      setOpenCategories([NodeCategory.AGENT]);
    }
  }, [debouncedSearch, filteredNodes]);

  const hasResults = useMemo(() => {
    return Object.entries(filteredNodes).some(
      ([, items]) => items && items.length > 0,
    );
  }, [filteredNodes]);

  return (
    <div className='flex min-h-0 h-full flex-col overflow-hidden'>
      <Tabs
        value={activeTab}
        onValueChange={(value) => setActiveTab(value as SidebarTab)}
        className='flex min-h-0 h-full flex-col'
      >
        <div className='shrink-0'>
          <div className='flex items-center justify-between'>
            {headerComponent}
          </div>
        </div>

        <div className='border-b'>
          <TabsList className='h-8 m-2 bg-background'>
            <TabsTrigger
              value='nodes'
              className={tabTriggerClasses}
              tabIndex={0}
            >
              Nodes
            </TabsTrigger>
            <TabsTrigger
              value='chat'
              className={tabTriggerClasses}
              tabIndex={0}
            >
              Chat
            </TabsTrigger>
          </TabsList>
        </div>

        {isWorkflowLocked && onUnlockWorkflow && (
          <div className='shrink-0 px-3 pt-2'>
            <WorkflowLockBanner
              isWorkflowAdmin={isWorkflowAdmin ?? false}
              onUnlock={onUnlockWorkflow}
            />
          </div>
        )}

        {/* Nodes Tab Content */}
        <TabsContent
          value='nodes'
          className='mt-0 flex min-h-0 flex-1 flex-col'
        >
          {isLoadingNodeList ? (
            <div className='flex flex-col gap-4 p-4'>
              <Skeleton className='h-9 w-full rounded-md' />
              {Array.from({ length: 3 }).map((_, groupIdx) => (
                <div key={groupIdx} className='flex flex-col gap-1.5'>
                  <Skeleton className='h-4 w-24 mb-1' />
                  {Array.from({ length: 3 }).map((_, itemIdx) => (
                    <Skeleton
                      key={itemIdx}
                      className={`h-8 ${itemIdx === 2 ? 'w-3/4' : 'w-full'}`}
                    />
                  ))}
                </div>
              ))}
            </div>
          ) : (
            <>
              <div className='shrink-0 p-4'>
                <div className='relative'>
                  <Search className='absolute left-3 top-1/2 size-4 -translate-y-1/2 transform text-muted-foreground' />
                  <Input
                    placeholder='Search nodes...'
                    value={search}
                    onChange={handleInputChange}
                    onKeyDown={handleSearchKeyDown}
                    onBlur={() => setHighlightedIndex(-1)}
                    className='pl-10 pr-8'
                  />
                  {search && (
                    <Button
                      variant='ghost'
                      size='icon'
                      onClick={handleClearSearch}
                      className='absolute right-1 top-1/2 size-6 -translate-y-1/2 transform'
                    >
                      <X className='size-3' />
                    </Button>
                  )}
                </div>
              </div>
              <div
                ref={listContainerRef}
                className='flex-1 min-h-0 overflow-y-auto pb-4'
                onClick={clearSidebarSelection}
              >
                {hasResults ? (
                  <NodeList
                    dataFilter={filteredNodes}
                    sortedCategories={sortedCategories}
                    openCategories={openCategories}
                    setOpenCategories={setOpenCategories}
                    search={search}
                    onDragStart={handleDragStart}
                    onAddNode={handleAddNode}
                    onDragStartCategory={handleDragStartCategory}
                    onAddCategory={handleAddCategory}
                    handleKeyDownInput={handleKeyDownInput}
                    isLocked={isLocked}
                    allowedSubTypes={allowedSubTypes}
                    highlightedIndex={highlightedIndex}
                    onFlatDisplayOrderChange={setFlatDisplayOrderFromList}
                    isNodeSelected={isSidebarNodeSelected}
                    getNodeSelectionCount={getSidebarSelectionCount}
                    onToggleSelect={handleToggleSelect}
                    onSelectSingle={handleSelectSingle}
                    onShiftSelect={handleShiftSelect}
                    onSelectCategory={handleSelectCategory}
                    onAddToSelection={handleAddToSelection}
                    onRangeSelect={handleRangeSelect}
                    onDragEnd={clearSidebarSelection}
                    initialSubCategoryState={DEFAULT_EXPANDED_MCP_SERVERS}
                  />
                ) : (
                  <NoResultsMessage
                    onClearSearch={handleClearSearch}
                    hasSearchQuery={search.length > 0}
                  />
                )}
              </div>
              <SelectionFooter
                totalCount={sidebarTotalSelectedCount}
                onAddAll={handleAddAllSelected}
              />
            </>
          )}
        </TabsContent>

        {/* Chat Tab Content */}
        <TabsContent
          value='chat'
          className='mt-0 flex min-h-0 flex-1 flex-col overflow-hidden'
        >
          {isCanvasA2aChatEnabled ? (
            <CanvasAgentChat
              workflowId={workflowId ?? null}
              onApplyMutations={applyCanvasChatMutations}
              applyMutationsDisabled={isChatEditingLocked}
              applyMutationsDisabledReason={applyDisabledReason}
              onRunWorkflow={onRunWorkflow}
            />
          ) : (
            <>
              {/* Chat status header */}
              <div className='flex min-w-0 items-center gap-2 px-3 pt-1.5 pb-2.5 mb-2'>
                {isChatConnecting || isGeneralChatConnecting ? (
                  <Loader2 className='size-3 animate-spin text-zinc-500' />
                ) : (
                  <div
                    className={`size-2 rounded-full ${
                      effectiveChatMode === 'node'
                        ? isChatConnected
                          ? 'bg-green-500'
                          : 'bg-zinc-400'
                        : isGeneralChatActive
                          ? 'bg-green-500'
                          : 'bg-zinc-400'
                    }`}
                  />
                )}
                <span className='min-w-0 flex-1 truncate text-xs leading-none text-zinc-500 dark:text-zinc-400'>
                  {effectiveChatMode === 'node' ? (
                    <>
                      Live chat with{' '}
                      <span className='font-medium text-zinc-700 dark:text-zinc-300'>
                        {selectedNodeDisplayName || 'node'}
                      </span>
                    </>
                  ) : !selectedNodeId ? (
                    isGeneralChatActive ? (
                      'Chatting with workflow'
                    ) : isGeneralChatConnecting ? (
                      'Connecting...'
                    ) : (
                      'Chatting with workflow'
                    )
                  ) : isChatConnecting || isGeneralChatConnecting ? (
                    <>
                      Connecting to{' '}
                      <span className='font-medium text-zinc-700 dark:text-zinc-300'>
                        {selectedNodeDisplayName}
                      </span>
                      ...
                    </>
                  ) : isGeneralChatActive ? (
                    <>
                      Chatting about{' '}
                      <span className='font-medium text-zinc-700 dark:text-zinc-300'>
                        {selectedNodeDisplayName}
                      </span>
                    </>
                  ) : (
                    <>
                      <span className='font-medium text-zinc-700 dark:text-zinc-300'>
                        {selectedNodeDisplayName}
                      </span>{' '}
                      selected
                    </>
                  )}
                </span>
              </div>

              {/* Chat Content — unified message stream for both modes */}
              <div className='min-h-0 flex-1'>
                {ENABLE_CHAT ? (
                  <Chat
                    messages={chatMessages}
                    onSendMessage={handleSendMessage}
                    onOpenArtifact={onOpenArtifact}
                    // The submit-clear onInputChange('') is what drives the
                    // idle-resume timer.
                    onInputChange={onInputChange}
                    onApplyUpdates={handleApplyUpdates}
                    onApplyWorkflowMutations={handleApplyWorkflowMutations}
                    onConfirmRemoveNode={handleConfirmRemoveNode}
                    clarificationAnswerControl={
                      onDeliverClarification
                        ? {
                            onDeliverAnswer: handleDeliverAnswer,
                          }
                        : undefined
                    }
                    onFocusNode={handleFocusClarificationNode}
                    resolveNodeName={resolveClarificationNodeName}
                    resolveNodeIcon={resolveClarificationNodeIcon}
                    onClarificationCardHoverChange={
                      // Hover-pause: only wire when both pause/resume
                      // callbacks exist, else it fires against noop stubs —
                      // pointless WS chatter and confusing BE-log breadcrumbs.
                      onPauseClarificationCountdown &&
                      onResumeClarificationCountdown
                        ? setCardHoverState
                        : undefined
                    }
                    // Busy no longer disables the composer -- it flips the
                    // send action to enqueue (see onQueueMessage); disabled
                    // is reserved for genuine unavailability.
                    isLoading={isAgentBusy}
                    disabled={
                      effectiveChatMode === 'node'
                        ? !isChatConnected || isChatDenied
                        : !isGeneralChatActive || isGeneralChatConnecting
                    }
                    onQueueMessage={promptQueue.enqueue}
                    registerComposerDraftSetter={registerComposerDraftSetter}
                    placeholder={chatPlaceholder}
                    applyDisabled={isChatEditingLocked}
                    applyDisabledReason={applyDisabledReason}
                    focusTrigger={
                      effectiveChatMode === 'node'
                        ? isChatAwaitingInput && isChatConnected
                        : isGeneralChatActive && isGeneralChatReady
                    }
                    aboveInput={
                      <>
                        {/* Hide the await-input banner only when THIS node's
                            clarification card is up (it already shows the
                            input); a different node's clarification must not
                            hide it. */}
                        {activeClarification?.nodeId ===
                        selectedNodeId ? null : effectiveChatMode === 'node' &&
                          isChatAwaitingInput &&
                          isChatConnected ? (
                          <div className='flex items-center justify-between gap-2 border-t px-4 pt-2 pb-2'>
                            <span className='text-xs text-zinc-900 dark:text-white'>
                              Agent is waiting for your input
                            </span>
                            <Button
                              size='sm'
                              variant='ghost'
                              onClick={handleContinue}
                              className='bg-blue-100 hover:bg-blue-200 text-blue-700 dark:bg-blue-900/50 dark:hover:bg-blue-900 dark:text-blue-300'
                            >
                              {RESUME_WORKFLOW_MESSAGE}
                            </Button>
                          </div>
                        ) : undefined}
                        <ChatPromptQueueStrip
                          className='mx-7 border-border bg-sidebar'
                          queueKey={chatQueueKey}
                          isBusy={isAgentBusy}
                          canEdit={!hasPendingClarificationCard}
                          onResume={promptQueue.resume}
                          onEditItem={setComposerDraft}
                        />
                      </>
                    }
                    emptyState={
                      <div className='flex flex-col items-center justify-center text-center text-sm text-zinc-500 dark:text-zinc-400'>
                        {!selectedNodeId ? (
                          isGeneralChatConnecting ? (
                            <>
                              <p className='font-medium text-zinc-900 dark:text-zinc-100'>
                                Connecting...
                              </p>
                              <p className='mt-1 max-w-[220px]'>
                                Setting up Operator.
                              </p>
                            </>
                          ) : (
                            <>
                              <p className='font-medium text-zinc-900 dark:text-zinc-100'>
                                Operator
                              </p>
                              <p className='mt-1 max-w-[220px]'>
                                I can help you build workflows, add nodes,
                                configure them, and wire everything together.
                              </p>
                            </>
                          )
                        ) : isGeneralChatConnecting ? (
                          <>
                            <p className='font-medium text-zinc-900 dark:text-zinc-100'>
                              Connecting...
                            </p>
                            <p className='mt-1 max-w-[220px]'>
                              Setting up chat for{' '}
                              {selectedNodeDisplayName || 'this node'}.
                            </p>
                          </>
                        ) : (
                          <>
                            <p className='font-medium text-zinc-900 dark:text-zinc-100'>
                              Chat with Operator about{' '}
                              {selectedNodeDisplayName || 'this node'}
                            </p>
                            <p className='mt-1 max-w-[220px]'>
                              I can help you write system prompts, brainstorm
                              instructions, or configure this node.
                            </p>
                          </>
                        )}
                      </div>
                    }
                    footerLeft={
                      // The picker is general-chat-only, but the clarification
                      // toggle always renders: it applies to every LLM node.
                      <>
                        {effectiveChatMode === 'general' ? (
                          <div className='relative' ref={modelSelectorRef}>
                            <button
                              onClick={() => setShowModelSelector((v) => !v)}
                              disabled={
                                isGeneralChatInputBusy ||
                                isGeneralChatConnecting
                              }
                              className={cn(
                                'flex items-center gap-1 rounded-2xl border border-zinc-200 bg-white px-2.5 py-1 text-[11px] text-zinc-600 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-400',
                                isGeneralChatInputBusy ||
                                  isGeneralChatConnecting
                                  ? 'cursor-not-allowed opacity-50'
                                  : 'hover:bg-zinc-50 dark:hover:bg-zinc-700',
                              )}
                            >
                              {GENERAL_CHAT_MODELS.find(
                                (m) => m.id === selectedModel,
                              )?.label || selectedModel}
                              <ChevronDown className='size-3' />
                            </button>
                            {showModelSelector && (
                              <div className='absolute bottom-full left-0 z-50 mb-1 w-44 rounded-md border border-zinc-200 bg-white py-1 shadow-lg dark:border-zinc-700 dark:bg-zinc-800'>
                                {GENERAL_CHAT_MODELS.map((m) => (
                                  <button
                                    key={m.id}
                                    onClick={() => {
                                      setSelectedModel(
                                        m.id as GeneralChatModelId,
                                      );
                                      setShowModelSelector(false);
                                    }}
                                    className={cn(
                                      'flex w-full items-center justify-between px-3 py-1.5 text-left text-xs hover:bg-zinc-100 dark:hover:bg-zinc-700',
                                      selectedModel === m.id
                                        ? 'text-blue-600 dark:text-blue-400 font-medium'
                                        : 'text-zinc-700 dark:text-zinc-300',
                                    )}
                                  >
                                    <span>{m.label}</span>
                                    <span className='text-[10px] text-zinc-400'>
                                      {m.provider}
                                    </span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        ) : null}
                        <ClarificationSettingsButton />
                      </>
                    }
                  />
                ) : (
                  <div className='flex h-full items-center justify-center text-sm text-zinc-500 dark:text-zinc-400'>
                    Chat feature coming soon!
                  </div>
                )}
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export const NodeExplorerSidebar = React.memo<NodeExplorerSidebarProps>(
  ({
    headerComponent,
    panelGroupWidth: _panelGroupWidth = 1400,
    isLocked = false,
    allowedSubTypes,
    isWorkflowLocked,
    isWorkflowAdmin,
    onUnlockWorkflow,
    pendingClarifications,
    onDeliverClarification,
    onPauseClarificationCountdown,
    onResumeClarificationCountdown,
    resolvedClarifications,
    onConsumeResolvedClarification,
    runSessionId,
    isWorkflowPaused,
    onRunWorkflow,
    chatRunEndedSignal,
    onOpenArtifact,
    onChatRunSignalConsumed,
  }) => {
    const {
      isMaximized,
      setMaximized,
      isHoveringWhileMinimized,
      panelSize,
      setPanelSize,
    } = useNodeExplorerStore();

    const { handleResizeStart, panelWidth } = useResizablePanel({
      panelSize,
      setPanelSize,
      edge: 'right',
      minSize: NODE_EXPLORER_PANEL_MIN_WIDTH,
      maxSize: NODE_EXPLORER_PANEL_MAX_WIDTH,
    });

    const handleClosePanel = useCallback(() => {
      setMaximized(false);
    }, [setMaximized]);

    const showFloatingPanel = !isMaximized && isHoveringWhileMinimized;
    const showMinimized = !isMaximized && !isHoveringWhileMinimized;

    if (showMinimized) {
      return (
        <div className='absolute top-2 left-2 z-50'>
          <div
            className={cn(
              'px-2 py-[7px] rounded-xl shadow-sm relative',
              'bg-background border border-border',
              'flex items-center gap-2',
            )}
          >
            <div className='min-w-0 overflow-hidden max-w-[350px]'>
              {headerComponent}
            </div>
            {isWorkflowLocked && (
              <div className='flex shrink-0 items-center gap-1 text-amber-600 dark:text-amber-400'>
                <Lock className='size-3.5' />
                <span className='text-xs font-medium'>Locked</span>
              </div>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant='ghost'
                  size='icon'
                  onClick={() => setMaximized(true)}
                  className='shrink-0'
                  aria-label='Maximize left panel'
                >
                  <Maximize2 className='size-4 dark:text-white' />
                </Button>
              </TooltipTrigger>
              <TooltipContent side='bottom'>
                <div className='text-center'>Maximize</div>
              </TooltipContent>
            </Tooltip>
          </div>
        </div>
      );
    }

    if (showFloatingPanel) {
      return (
        <div
          className='absolute top-2 left-2 z-50'
          style={{ width: panelWidth, height: 'calc(100% - 16px)' }}
        >
          <div
            className={cn(
              'h-full rounded-xl shadow-lg',
              'bg-background border border-border',
              'flex flex-col overflow-hidden',
            )}
          >
            <div className='p-2 shrink-0'>
              <div className='flex items-center justify-between'>
                {headerComponent}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant='ghost'
                      size='icon'
                      onClick={() => setMaximized(true)}
                      className='size-8'
                      aria-label='Maximize left panel'
                    >
                      <Maximize2 className='size-4' />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side='right'>
                    <div className='text-center'>Maximize</div>
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>

            <div className='flex-1 min-h-0 overflow-hidden'>
              <NodeExplorerSidebarContent
                headerComponent={null}
                isLocked={isLocked}
                allowedSubTypes={allowedSubTypes}
                isWorkflowLocked={isWorkflowLocked}
                isWorkflowAdmin={isWorkflowAdmin}
                onUnlockWorkflow={onUnlockWorkflow}
                pendingClarifications={pendingClarifications}
                onDeliverClarification={onDeliverClarification}
                onPauseClarificationCountdown={onPauseClarificationCountdown}
                onResumeClarificationCountdown={onResumeClarificationCountdown}
                resolvedClarifications={resolvedClarifications}
                onConsumeResolvedClarification={onConsumeResolvedClarification}
                runSessionId={runSessionId}
                isWorkflowPaused={isWorkflowPaused}
                onRunWorkflow={onRunWorkflow}
                chatRunEndedSignal={chatRunEndedSignal}
                onOpenArtifact={onOpenArtifact}
                onChatRunSignalConsumed={onChatRunSignalConsumed}
              />
            </div>
          </div>

          <div
            className='absolute right-0 top-2 bottom-2 w-px cursor-ew-resize bg-border z-10 flex items-center justify-center after:absolute after:inset-y-0 after:right-1/2 after:w-1 after:translate-x-1/2'
            onMouseDown={handleResizeStart}
          >
            <div className='bg-border z-10 flex h-4 w-3 items-center justify-center rounded-xs border'>
              <GripVerticalIcon className='size-2.5' />
            </div>
          </div>
        </div>
      );
    }

    return (
      <div
        className='absolute top-0 left-0 bottom-0 z-40'
        style={{ width: panelWidth }}
      >
        <div
          className='absolute right-0 top-0 bottom-0 w-px cursor-ew-resize bg-border z-10 flex items-center justify-center after:absolute after:inset-y-0 after:right-1/2 after:w-1 after:translate-x-1/2'
          onMouseDown={handleResizeStart}
        >
          <div className='bg-border z-10 flex h-4 w-3 items-center justify-center rounded-xs border'>
            <GripVerticalIcon className='size-2.5' />
          </div>
        </div>
        <div className='h-full'>
          <div className='h-full bg-background border-r shadow-lg flex flex-col overflow-hidden'>
            <div className='p-2 shrink-0'>
              <div className='flex items-center justify-between'>
                {headerComponent}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant='ghost'
                      size='icon'
                      onClick={handleClosePanel}
                      className='size-8'
                      aria-label='Close left panel'
                    >
                      <X className='size-4' />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side='right'>
                    <div className='text-center'>Close</div>
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>

            <div className='flex-1 min-h-0 overflow-hidden'>
              <NodeExplorerSidebarContent
                headerComponent={null}
                isLocked={isLocked}
                allowedSubTypes={allowedSubTypes}
                isWorkflowLocked={isWorkflowLocked}
                isWorkflowAdmin={isWorkflowAdmin}
                onUnlockWorkflow={onUnlockWorkflow}
                pendingClarifications={pendingClarifications}
                onDeliverClarification={onDeliverClarification}
                onPauseClarificationCountdown={onPauseClarificationCountdown}
                onResumeClarificationCountdown={onResumeClarificationCountdown}
                resolvedClarifications={resolvedClarifications}
                onConsumeResolvedClarification={onConsumeResolvedClarification}
                runSessionId={runSessionId}
                isWorkflowPaused={isWorkflowPaused}
                onRunWorkflow={onRunWorkflow}
                chatRunEndedSignal={chatRunEndedSignal}
                onOpenArtifact={onOpenArtifact}
                onChatRunSignalConsumed={onChatRunSignalConsumed}
              />
            </div>
          </div>
        </div>
      </div>
    );
  },
);
